import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { MessageCircle, Plus, Edit, Trash2, Star, Clock, MapPin, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from "react-helmet-async";

interface SentenceFormData {
  sentence: string;
  usage_context: string;
  time_context: string;
  example_situation: string;
  category: string;
  difficulty_level: 'beginner' | 'intermediate' | 'advanced';
  seo_title: string;
  meta_description: string;
  keywords: string;
  display_order: number;
  is_active: boolean;
}

const SentencesManager = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<SentenceFormData>({
    sentence: '',
    usage_context: '',
    time_context: '',
    example_situation: '',
    category: 'general',
    difficulty_level: 'beginner',
    seo_title: '',
    meta_description: '',
    keywords: '',
    display_order: 0,
    is_active: true,
  });

  const queryClient = useQueryClient();

  // Fetch sentences
  const { data: sentences, isLoading } = useQuery({
    queryKey: ['admin-sentences'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sentences_of_the_day')
        .select('*')
        .order('display_order');
      if (error) throw error;
      return data;
    },
  });

  // Fetch sentence of the day
  const { data: sentenceOfTheDay } = useQuery({
    queryKey: ['admin-sentence-of-the-day'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sentence_of_the_day')
        .select('*')
        .eq('is_active', true)
        .single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // Create/Update sentence mutation
  const createUpdateMutation = useMutation({
    mutationFn: async (data: SentenceFormData) => {
      const keywordsArray = data.keywords ? data.keywords.split(',').map(k => k.trim()) : [];
      
      if (editingId) {
        const { error } = await supabase
          .from('sentences_of_the_day')
          .update({
            ...data,
            keywords: keywordsArray,
          })
          .eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('sentences_of_the_day')
          .insert({
            ...data,
            keywords: keywordsArray,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sentences'] });
      setIsDialogOpen(false);
      resetForm();
      toast.success(editingId ? 'Sentence updated successfully!' : 'Sentence created successfully!');
    },
    onError: (error) => {
      toast.error('Error saving sentence: ' + error.message);
    },
  });

  // Delete sentence mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('sentences_of_the_day')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sentences'] });
      toast.success('Sentence deleted successfully!');
    },
    onError: (error) => {
      toast.error('Error deleting sentence: ' + error.message);
    },
  });

  // Set sentence of the day mutation
  const setSentenceOfTheDayMutation = useMutation({
    mutationFn: async (sentenceId: string) => {
      // First, deactivate current sentence of the day
      await supabase
        .from('sentence_of_the_day')
        .update({ is_active: false })
        .eq('is_active', true);

      // Then set new sentence of the day
      const { error } = await supabase
        .from('sentence_of_the_day')
        .upsert({
          sentence_id: sentenceId,
          is_active: true,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-sentence-of-the-day'] });
      toast.success('Sentence of the day updated!');
    },
    onError: (error) => {
      toast.error('Error setting sentence of the day: ' + error.message);
    },
  });

  const resetForm = () => {
    setFormData({
      sentence: '',
      usage_context: '',
      time_context: '',
      example_situation: '',
      category: 'general',
      difficulty_level: 'beginner',
      seo_title: '',
      meta_description: '',
      keywords: '',
      display_order: 0,
      is_active: true,
    });
    setEditingId(null);
  };

  const handleEdit = (sentence: any) => {
    setFormData({
      sentence: sentence.sentence,
      usage_context: sentence.usage_context,
      time_context: sentence.time_context || '',
      example_situation: sentence.example_situation || '',
      category: sentence.category,
      difficulty_level: sentence.difficulty_level,
      seo_title: sentence.seo_title || '',
      meta_description: sentence.meta_description || '',
      keywords: sentence.keywords ? sentence.keywords.join(', ') : '',
      display_order: sentence.display_order,
      is_active: sentence.is_active,
    });
    setEditingId(sentence.id);
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createUpdateMutation.mutate(formData);
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading sentences...</div>;
  }

  return (
    <div className="space-y-6">
      <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <MessageCircle className="text-tinyminds-coral" />
          Sentences of the Day Manager
        </h2>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="bg-tinyminds-coral hover:bg-tinyminds-coral/90">
              <Plus className="mr-2 h-4 w-4" />
              Add New Sentence
            </Button>
          </DialogTrigger>
          
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingId ? 'Edit Sentence' : 'Add New Sentence'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="sentence">Sentence *</Label>
                <Textarea
                  id="sentence"
                  value={formData.sentence}
                  onChange={(e) => setFormData(prev => ({ ...prev, sentence: e.target.value }))}
                  placeholder="Enter the sentence..."
                  required
                />
              </div>

              <div>
                <Label htmlFor="usage_context">Where to use it *</Label>
                <Input
                  id="usage_context"
                  value={formData.usage_context}
                  onChange={(e) => setFormData(prev => ({ ...prev, usage_context: e.target.value }))}
                  placeholder="e.g., At home, school, or with friends"
                  required
                />
              </div>

              <div>
                <Label htmlFor="time_context">When to use it</Label>
                <Input
                  id="time_context"
                  value={formData.time_context}
                  onChange={(e) => setFormData(prev => ({ ...prev, time_context: e.target.value }))}
                  placeholder="e.g., Morning greeting, During play"
                />
              </div>

              <div>
                <Label htmlFor="example_situation">Example Situation</Label>
                <Textarea
                  id="example_situation"
                  value={formData.example_situation}
                  onChange={(e) => setFormData(prev => ({ ...prev, example_situation: e.target.value }))}
                  placeholder="Describe when this sentence would be used..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Input
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., greetings, gratitude"
                  />
                </div>

                <div>
                  <Label htmlFor="difficulty_level">Difficulty Level</Label>
                  <Select
                    value={formData.difficulty_level}
                    onValueChange={(value: 'beginner' | 'intermediate' | 'advanced') =>
                      setFormData(prev => ({ ...prev, difficulty_level: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator />
              <h4 className="font-semibold">SEO Settings</h4>

              <div>
                <Label htmlFor="seo_title">SEO Title</Label>
                <Input
                  id="seo_title"
                  value={formData.seo_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, seo_title: e.target.value }))}
                  placeholder="SEO-friendly title for this sentence"
                />
              </div>

              <div>
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  value={formData.meta_description}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                  placeholder="Brief description for search engines"
                />
              </div>

              <div>
                <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                <Input
                  id="keywords"
                  value={formData.keywords}
                  onChange={(e) => setFormData(prev => ({ ...prev, keywords: e.target.value }))}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="display_order">Display Order</Label>
                  <Input
                    id="display_order"
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="flex items-center space-x-2 pt-6">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-tinyminds-coral hover:bg-tinyminds-coral/90">
                  {editingId ? 'Update' : 'Create'} Sentence
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {sentences?.map((sentence) => (
          <Card key={sentence.id} className="p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-lg">"{sentence.sentence}"</h3>
                  {sentenceOfTheDay?.sentence_id === sentence.id && (
                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                      <Star className="w-3 h-3 mr-1" />
                      Current
                    </Badge>
                  )}
                  <Badge className={getDifficultyColor(sentence.difficulty_level)}>
                    {sentence.difficulty_level}
                  </Badge>
                  <Badge variant="outline">{sentence.category}</Badge>
                  {!sentence.is_active && (
                    <Badge variant="destructive">Inactive</Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-green-600" />
                    <span><strong>Where:</strong> {sentence.usage_context}</span>
                  </div>
                  {sentence.time_context && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span><strong>When:</strong> {sentence.time_context}</span>
                    </div>
                  )}
                  {sentence.example_situation && (
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-yellow-600" />
                      <span><strong>Example:</strong> {sentence.example_situation}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 ml-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSentenceOfTheDayMutation.mutate(sentence.id)}
                  disabled={sentenceOfTheDay?.sentence_id === sentence.id}
                >
                  <Star className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleEdit(sentence)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteMutation.mutate(sentence.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SentencesManager;